import React from 'react'
import { ButtonStyle, ButtonShape, Button } from '../../../components/simple/button'
import { Dropdown, DropdownItem, DropdownSubmenu, dropdownItemStyle } from '../../../components/simple/dropdown'
import { IconCaretRight, IconSchool, IconTrash } from '../../../icons/library'
import { ComposeState, DEFAULT_COMPOSE_STATE } from './composeTypes'
import { prepareDeltaToPost } from '../../../utils'
import { Toasts } from '../../../providers/toast'
import { Popups } from '../../../providers/popups'
import { makeRequest, showAxiosErrorAsToast, useCachedRequest } from '../../../request'
import { useAppContext } from '../../../providers/context'

type Template = {
  id?: number
  subject: string
  message: string
  shared?: boolean
}

interface TemplatesRowProps {
  showSubject: boolean
  composeState: ComposeState
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
}

export const TemplatesRow: React.FC<TemplatesRowProps> = ({ composeState, setComposeState, showSubject }) => {
  const {
    state: { user },
  } = useAppContext()
  const {
    data: templates,
    reload,
    loading: loadingInitial,
  } = useCachedRequest<Record<string, Template[]>>('/frontend_api/getTemplates', user?.id)
  const [loadingOtherCall, setLoadingOtherCall] = React.useState(false)

  const saveTemplate = async () => {
    const canShare = user?.type === 'teacher' && user.admin && (user.districtAccess || user?.schools.length === 1)
    const shareWithDistrict = canShare && user?.type === 'teacher' && user?.districtAccess
    let share = false

    const { subject, bodyHTML } = composeState
    let name = subject
    if (subject === DEFAULT_COMPOSE_STATE.subject || !showSubject) {
      if (canShare) {
        const result = (await Popups.prompt({
          title: 'Enter template name',
          confirm: 'Save template',
          cancel: 'Cancel',
          checkbox: {
            label: `Share with your ${shareWithDistrict ? 'district' : 'school'}`,
          },
        })) ?? ['', false]
        name = result[0]
        share = result[1]
      } else {
        name =
          (await Popups.prompt({
            title: 'Enter template name',
            confirm: 'Save template',
            cancel: 'Cancel',
          })) ?? ''
      }
    } else if (canShare) {
      share = !(await Popups.confirm({
        title: `Do you want to share this template with your ${shareWithDistrict ? 'district' : 'school'}?`,
        yesText: 'No',
        noText: 'Yes',
      }))
    }

    if (!name.trim()) return

    setLoadingOtherCall(true)
    try {
      await makeRequest('/frontend_api/saveTemplate', { subject: name, content: bodyHTML, share })
      Toasts.showToast({ message: 'Template saved!' })
      reload()
    } catch (e) {
      showAxiosErrorAsToast(e)
    } finally {
      setLoadingOtherCall(false)
    }
  }

  const deleteTemplate = (template: Template) => {
    Popups.simpleConfirm(`Are you sure you want to delete this ${template.shared ? 'shared ' : ''}template?`).then(
      (confirmed) => {
        if (!confirmed) return
        setLoadingOtherCall(true)
        makeRequest('/frontend_api/deleteTemplate', { id: template.id })
          .then(reload)
          .catch(showAxiosErrorAsToast)
          .finally(() => setLoadingOtherCall(false))
      }
    )
  }

  const writingAssistant = () => {
    setLoadingOtherCall(true)
    makeRequest<{ text: string; status: string }>('/frontend_api/getAISuggestion', {
      message: prepareDeltaToPost(composeState.bodyDelta),
    })
      .then((response) => {
        if (response.status !== 'success') {
          Toasts.showToast({ subject: 'AI suggestion failed', message: response.text, error: true })
          return
        }
        setComposeState(
          (cs) =>
            cs && {
              ...cs,
              aiSuggestion: response.text,
            }
        )
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoadingOtherCall(false))
  }

  const loading = loadingInitial || loadingOtherCall

  const emptySubject = composeState.subject === DEFAULT_COMPOSE_STATE.subject || !showSubject
  const emptyBody = composeState.bodyHTML === DEFAULT_COMPOSE_STATE.bodyHTML

  return (
    <div className="flex justify-end gap-2">
      {(emptySubject && emptyBody) || composeState.previewingTemplate ? (
        <Dropdown
          onOpenChange={(o) => {
            // @ts-ignore - this is a custom method I attach to the editor to fix a weird bug that occurs when the
            // dropdown is opened while the editor is focused. When the template preview changes the editor content,
            // the dropdown immediately closes. We fix it by blurring the editor when the dropdown is opened.
            if (o && document.activeElement?.classList.contains('ql-editor') && document.activeElement.blurQuill)
              //@ts-ignore
              document.activeElement.blurQuill()
          }}
          button={<RowButton disabled={loading}>Templates</RowButton>}
        >
          {Object.entries(templates || {})
            .filter(([name, groupTemplates]) => groupTemplates.length > 0)
            .map(([name, groupTemplates]) => (
              <DropdownSubmenu
                button={
                  <div className={`${dropdownItemStyle} justify-between text-sm text-charcoal-light`}>
                    {name === 'custom' ? (
                      <b>Custom</b>
                    ) : name.startsWith('SHARED:') ? (
                      <b>
                        <IconSchool className="mr-1" />
                        {name.replace('SHARED:', '')}
                      </b>
                    ) : (
                      <span className="font-semibold">{name}</span>
                    )}
                    <IconCaretRight size="sm" />
                  </div>
                }
                key={name}
              >
                {groupTemplates.map((template, index) => (
                  <TemplateButton
                    key={index}
                    template={template}
                    setComposeState={setComposeState}
                    onDelete={template.id !== undefined ? deleteTemplate : undefined}
                  />
                ))}
              </DropdownSubmenu>
            ))}
        </Dropdown>
      ) : (
        <>
          {!emptyBody && (
            <RowButton onClick={saveTemplate} disabled={loading}>
              Save template
            </RowButton>
          )}
          {composeState.aiSuggestion ? (
            <>
              <RowButton
                disabled={loading}
                onClick={() => {
                  setComposeState(
                    (cs) =>
                      cs && {
                        ...cs,
                        bodyHTML: cs.aiSuggestion!,
                        aiSuggestion: undefined,
                      }
                  )
                }}
              >
                Confirm Suggestion
              </RowButton>
              <RowButton
                disabled={loading}
                onClick={() => {
                  setComposeState(
                    (cs) =>
                      cs && {
                        ...cs,
                        aiSuggestion: undefined,
                      }
                  )
                }}
              >
                Cancel
              </RowButton>
            </>
          ) : (
            <RowButton disabled={loading} onClick={writingAssistant}>
              Writing assistant
            </RowButton>
          )}
        </>
      )}
    </div>
  )
}

const RowButton = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ children, ...props }, ref) => {
    return (
      <Button
        buttonStyle={ButtonStyle.ghostLightBlue}
        shape={ButtonShape.medium}
        className="text-sm"
        ref={ref}
        {...props}
      >
        {children}
      </Button>
    )
  }
)

const TemplateButton: React.FC<{
  template: Template
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
  onDelete?: (template: Template) => void
}> = ({ template, setComposeState, onDelete }) => {
  const [hovered, setHovered] = React.useState(false)
  const onHover = () => {
    setHovered(true)
    setComposeState(
      (cs) => cs && { ...cs, subject: template.subject, bodyHTML: template.message, previewingTemplate: true }
    )
  }
  const onLeave = () => {
    if (hovered) {
      setHovered(false)
      setComposeState(
        (cs) =>
          cs && {
            ...cs,
            subject: DEFAULT_COMPOSE_STATE.subject,
            bodyHTML: DEFAULT_COMPOSE_STATE.bodyHTML,
            previewingTemplate: false,
          }
      )
    }
  }
  const onClick = () => {
    setHovered(false)
    setComposeState((cs) => cs && { ...cs, previewingTemplate: false })
  }
  return (
    <div
      className="-my-0.5 py-0.5" // remove the gap between the items to prevent flickering when hovering over the gap
      onFocus={onHover}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onBlur={onLeave}
      onClick={onClick}
    >
      <DropdownItem className="justify-between text-sm text-charcoal-light">
        {template.subject}

        {onDelete && template.id !== undefined && (
          <IconTrash
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onDelete(template)
              onLeave()
            }}
            className="cursor-pointer opacity-30 hover:text-red-500 hover:opacity-100"
          />
        )}
      </DropdownItem>
    </div>
  )
}
